import React, { useEffect, useRef, useState, useCallback } from "react";
import "./S4VI.css";

import Shot1 from "../../assets/S4VIslide1.png";
import Shot2 from "../../assets/S4VIslide2.png";
import Shot3 from "../../assets/S4VIslide3.png";

const slides = [
  { img: Shot1, title: "Beautiful Modern Design", desc: "Our store is designed to be simple, intuitive, and focused entirely on e-commerce" },
  { img: Shot2, title: "Simple Product Dashboard", desc: "Add, edit, and remove products from your store with ease" },
  { img: Shot3, title: "Easy order fulfillment", desc: "Create invoices, print packing slips, and manage shipping in one place." }
];

export default function S4VI() {
  const viewportRef = useRef(null);
  const dotsRef = useRef([]);
  const [current, setCurrent] = useState(0);

  const getCards = () =>
    Array.from(viewportRef.current?.querySelectorAll(".s4vi-card") ?? []);

  const setActive = useCallback((idx) => {
    const cards = getCards();
    cards.forEach((el, i) => el.classList.toggle("is-active", i === idx));
    dotsRef.current.forEach((d, i) => {
      if (!d) return;
      d.classList.toggle("is-active", i === idx);
      d.setAttribute("aria-selected", String(i === idx));
    });
  }, []);

  const goTo = (idx) => {
    const cards = getCards();
    if (!cards.length) return;
    const n = cards.length;
    const safe = ((idx % n) + n) % n;
    cards[safe]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest" // prevent page vertical scroll
    });
    setCurrent(safe);
    setActive(safe);
  };

  const prev = () => goTo(current - 1);
  const next = () => goTo(current + 1);

  // init
  useEffect(() => {
    setActive(0);
  }, [setActive]);

  // 🔥 Touch/drag tracking: update active on scroll
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;

    let raf = 0;
    const onScroll = () => {
      // throttle with rAF
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const cards = getCards();
        if (!cards.length) return;

        const centerX = vp.scrollLeft + vp.clientWidth / 2;

        // pick the card whose center is closest to viewport center
        let best = 0;
        let bestDist = Infinity;
        for (let i = 0; i < cards.length; i++) {
          const el = cards[i];
          const mid = el.offsetLeft + el.offsetWidth / 2;
          const d = Math.abs(centerX - mid);
          if (d < bestDist) { bestDist = d; best = i; }
        }

        if (best !== current) {
          setCurrent(best);
          setActive(best);
        }
      });
    };

    vp.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      vp.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [current, setActive]);

  return (
    <section className="s4vi" aria-labelledby="s4vi-heading">
      <div className="s4vi-wrap">
        <h2 id="s4vi-heading" className="s4vi-heading">
          A simple, easy-to-use retail platform
        </h2>

        <div className="s4vi-stage">
          <button type="button" className="s4vi-nav s4vi-prev" aria-label="Previous" onClick={prev}>‹</button>

          <div className="s4vi-viewport" ref={viewportRef} aria-live="polite">
            {slides.map((s) => (
              <article key={s.title} className="s4vi-card">
                <div className="s4vi-shot">
                  <img src={s.img} alt="" loading="lazy" />
                </div>
                <footer className="s4vi-card-foot">
                  <h3 className="s4vi-card-title">{s.title}</h3>
                  <p className="s4vi-card-desc">{s.desc}</p>
                </footer>
              </article>
            ))}
          </div>

          <button type="button" className="s4vi-nav s4vi-next" aria-label="Next" onClick={next}>›</button>
        </div>

        <div className="s4vi-dots" role="tablist" aria-label="Slide dots">
          {slides.map((_, i) => (
            <button
              key={i}
              ref={(el) => (dotsRef.current[i] = el)}
              role="tab"
              className={`s4vi-dot ${i === 0 ? "is-active" : ""}`}
              aria-selected={i === 0 ? "true" : "false"}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => goTo(i)}
              type="button"
            />
          ))}
        </div>
      </div>
    </section>
  );
}
